import apiClient from './api';

/**
 * Teacher Management API Service
 * Updated: 2025-08-01 - Added import/export functionality
 */
export const teacherAPI = {
    /**
     * Get all teachers with optional filters
     * @param {Object} params - Filter parameters
     * @returns {Promise} API response
     */
    getTeachers: (params = {}) => {
        return apiClient.get('teachers', { params });
    },

    /**
     * Get a specific teacher by ID
     * @param {number} id - Teacher ID
     * @returns {Promise} API response
     */
    getTeacher: (id) => {
        return apiClient.get(`teachers/${id}`);
    },

    /**
     * Create a new teacher
     * @param {Object} data - Teacher data
     * @returns {Promise} API response
     */
    createTeacher: (data) => {
        return apiClient.post('teachers', data);
    },

    /**
     * Update an existing teacher
     * @param {number} id - Teacher ID
     * @param {Object} data - Updated teacher data
     * @returns {Promise} API response
     */
    updateTeacher: (id, data) => {
        return apiClient.put(`teachers/${id}`, data);
    },

    /**
     * Delete a teacher
     * @param {number} id - Teacher ID
     * @returns {Promise} API response
     */
    deleteTeacher: (id) => {
        return apiClient.delete(`teachers/${id}`);
    },

    /**
     * Get teacher assignments (subjects and sections) with optional available assignments
     * @param {number} id - Teacher ID
     * @param {Object} params - Additional parameters (e.g., include_available: true)
     * @returns {Promise} API response
     */
    getTeacherAssignments: (id, params = {}) => {
        return apiClient.get(`teachers/${id}/assignments`, { params });
    },

    /**
     * Get available assignments (classes, sections, subjects)
     * @param {Object} params - Filter parameters
     * @returns {Promise} API response
     */
    getAvailableAssignments: (params = {}) => {
        return apiClient.get('teachers/available-assignments', { params });
    },

    /**
     * Get subjects by class and section for teacher assignment
     * Optimized endpoint that returns only subjects for a specific class and section
     * @param {Object} params - { class_id, section_id, academic_id, institute_id }
     * @returns {Promise} API response
     */
    getSubjectsByClassSection: (params = {}) => {
        return apiClient.get('teachers/subjects-by-class-section', { params });
    },

    /**
     * Assign a subject to a teacher
     * @param {Object} data - Assignment data
     * @returns {Promise} API response
     */
    assignSubject: (data) => {
        return apiClient.post('teachers/assign-subject', data);
    },

    /**
     * Assign a class teacher to a section
     * @param {Object} data - Assignment data
     * @returns {Promise} API response
     */
    assignClassTeacher: (data) => {
        return apiClient.post('teachers/assign-class-teacher', data);
    },

    /**
     * Unassign a teacher from a subject
     * @param {number} subjectId - Subject ID
     * @returns {Promise} API response
     */
    unassignTeacher: (subjectId) => {
        return apiClient.post(`subjects/unassign-teacher/${subjectId}`);
    },

    /**
     * Get teachers analytics/statistics
     * @param {Object} params - Filter parameters
     * @returns {Promise} API response
     */
    getAnalytics: (params = {}) => {
        return apiClient.get('teachers-analytics', { params });
    },

    /**
     * Search teachers
     * @param {string} query - Search query
     * @param {Object} filters - Additional filters
     * @returns {Promise} API response
     */
    searchTeachers: (query, filters = {}) => {
        return apiClient.get('teachers', { 
            params: { 
                search: query,
                ...filters
            }
        });
    },

    /**
     * Get teachers by department
     * @param {string} department - Department name
     * @param {Object} params - Additional parameters
     * @returns {Promise} API response
     */
    getTeachersByDepartment: (department, params = {}) => {
        return apiClient.get('teachers', { 
            params: { 
                department,
                ...params
            }
        });
    },

    /**
     * Get teachers by designation
     * @param {string} designation - Designation name
     * @param {Object} params - Additional parameters
     * @returns {Promise} API response
     */
    getTeachersByDesignation: (designation, params = {}) => {
        return apiClient.get('teachers', { 
            params: { 
                designation,
                ...params
            }
        });
    },

    /**
     * Bulk operations for teachers
     */
    bulk: {
        /**
         * Export teachers data
         * @param {Object} params - Export parameters
         * @returns {Promise} API response
         */
        export: (params = {}) => {
            return apiClient.get('teachers/export', { 
                params,
                responseType: 'blob'
            });
        },

        /**
         * Import teachers data
         * @param {FormData} formData - Form data with file
         * @returns {Promise} API response
         */
        import: (formData) => {
            return apiClient.post('teachers/import', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
        },

        /**
         * Get import template
         * @returns {Promise} API response
         */
        getTemplate: () => {
            return apiClient.get('teachers/import/template', {
                responseType: 'blob'
            });
        }
    },

    /**
     * Teacher specific utilities
     */
    utils: {
        /**
         * Generate employee code
         * @param {Object} params - Parameters for code generation
         * @returns {Promise} API response
         */
        generateEmployeeCode: (params = {}) => {
            return apiClient.post('teachers/generate-employee-code', params);
        },

        /**
         * Validate teacher data
         * @param {Object} data - Teacher data to validate
         * @returns {Promise} API response
         */
        validateData: (data) => {
            return apiClient.post('teachers/validate', data);
        },

        /**
         * Check availability of email/mobile
         * @param {Object} data - Email/mobile to check
         * @returns {Promise} API response
         */
        checkAvailability: (data) => {
            return apiClient.post('teachers/check-availability', data);
        },

        /**
         * Get all departments for dropdown options
         * @param {Object} params - Optional filter parameters
         * @returns {Promise} API response
         */
        getDepartments: (params = {}) => {
            return apiClient.get('departments-options', { params });
        },

        /**
         * Download teacher import template
         * @returns {Promise} API response with file blob
         */
        downloadTemplate: () => {
            return apiClient.get('teachers/bulk-import/template', {
                responseType: 'blob'
            });
        },

        /**
         * Import teachers from Excel file
         * @param {FormData} formData - Form data with file
         * @returns {Promise} API response
         */
        importData: (formData) => {
            return apiClient.post('teachers/bulk-import', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
        },

        /**
         * Export all teachers data to Excel
         * @param {Object} params - Optional filter parameters
         * @returns {Promise} API response with file blob
         */
        exportData: (params = {}) => {
            return apiClient.get('teachers/bulk-export', {
                params,
                responseType: 'blob'
            });
        }
    }
};
